import { useState } from "react";
import { Dialog, Text, useToast } from "@flat-ui";
import { setFeatureInBundle } from "@api/admin-bundle-features-api";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { BundleFeatureChange } from "@pages/about-products/about-products-table-types";
import { ExperimentalFeatureEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface ChangeDisplay {
  /** Title of the dialog */
  title: string;

  /** Look and feel of the dialog */
  variant: "info" | "danger";

  /** Sentence fragment describing the change, e.g. "will be added to" */
  description: string;

  /** Toast title shown once the change was applied */
  successTitle: string;

  /** Toast title shown when the change could not be applied */
  errorTitle: string;
}

const CHANGE_DISPLAY: Record<"add" | "remove", ChangeDisplay> = {
  add: {
    title: "Add feature to bundle",
    variant: "info",
    description: "will be added to",
    successTitle: "The feature was added to the bundle",
    errorTitle: "Could not add the feature to the bundle",
  },
  remove: {
    title: "Remove feature from bundle",
    variant: "danger",
    description: "will be removed from",
    successTitle: "The feature was removed from the bundle",
    errorTitle: "Could not remove the feature from the bundle",
  },
};

interface Props {
  /** The change the user asked for and still has to confirm */
  change: BundleFeatureChange;

  /** Called after the change was applied successfully */
  onApplied(): void;

  /** Called when the user closes the dialog without applying the change */
  onClose(): void;
}

/** Asks the user to confirm adding a feature to a bundle or removing it from the bundle */
export function BundleFeatureChangeDialog({
  change,
  onApplied,
  onClose,
}: Props): React.JSX.Element {
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandler();
  const { trackEvent } = useTrackEvent();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const { bundle, featureId, featureName, shouldAdd } = change;
  const display = CHANGE_DISPLAY[shouldAdd ? "add" : "remove"];

  async function handleConfirm(): Promise<void> {
    try {
      trackEvent({
        name: ExperimentalFeatureEvents.changeBundleFeature,
        props: {
          bundleIdentifier: bundle.identifier,
          featureId,
          shouldAdd,
        },
      });
      setIsUpdating(true);

      await setFeatureInBundle({ bundleId: bundle.id, featureId, shouldAdd });

      openToast({ title: display.successTitle });
      onApplied();
    } catch (error) {
      handleErrorWithToast({
        id: `change-bundle-feature-${Date.now().toString()}`,
        title: display.errorTitle,
        error,
      });
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <Dialog
      open={true}
      title={display.title}
      onCancel={onClose}
      onConfirm={handleConfirm}
      showSpinner={isUpdating}
      variant={display.variant}
    >
      <Text variant="bodyL">
        The feature <strong>{featureName}</strong> {display.description} the
        bundle <strong>{bundle.name}</strong>.
      </Text>
    </Dialog>
  );
}
